import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { Logo } from "./Shared";
import { NAV_LINKS, waLink } from "../../lib/site";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-40 bg-ink/80 backdrop-blur-xl border-b border-white/10"
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo-link">
          <Logo />
        </a>

        <div className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm text-white/70 hover:text-gold transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-booking-button"
            className="btn-gold-fill hidden sm:inline-flex items-center gap-2 bg-gold text-ink px-5 py-2.5 text-sm font-medium tracking-wide rounded-sm hover:text-ink"
          >
            <MessageCircle size={16} />
            Make a Booking
          </a>
          <button
            onClick={() => setOpen(true)}
            className="lg:hidden text-white p-2"
            data-testid="nav-menu-open"
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-ink lg:hidden flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="h-20 px-6 flex items-center justify-between border-b border-white/10">
              <Logo />
              <button
                onClick={() => setOpen(false)}
                className="text-white p-2"
                data-testid="nav-menu-close"
                aria-label="Close menu"
              >
                <X size={24} />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-8 gap-2">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                  data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="font-heading text-4xl text-white/90 py-3 border-b border-white/5"
                >
                  {l.label}
                </motion.a>
              ))}
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-booking-button"
                className="mt-8 inline-flex items-center justify-center gap-2 bg-gold text-ink px-6 py-4 text-sm font-medium tracking-widest uppercase rounded-sm"
              >
                <MessageCircle size={18} /> Make a Booking
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
